import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { CaretLeft, CaretRight, Lightning, ShieldCheck, Clock, Question } from '@phosphor-icons/react';
import { getServiceBySlug, services } from '../data/services';
import ServiceContent, { getServiceColors } from './ServiceContent';
import ServiceEnquiryForm from './ServiceEnquiryForm';
import ChatNexShowcase from './ChatNexShowcase';
import { applyPageSeo, pageUrl, SITE_URL } from '../utils/seo';

const STEPS = [
  { icon: Lightning, title: 'Share your need', text: 'Tell us the goal, audience and timeline.' },
  { icon: Clock, title: 'We plan & build', text: 'Strategy, design and development in sprints.' },
  { icon: ShieldCheck, title: 'Launch & support', text: 'Secure go-live with ongoing care.' }
];

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const colors = service ? getServiceColors(service.color) : null;

  useEffect(() => {
    if (!service) return undefined;
    window.scrollTo({ top: 0, behavior: 'auto' });
    return applyPageSeo({
      title: service.seoTitle,
      description: service.seoDescription,
      keywords: service.seoKeywords,
      path: `/services/${service.slug}`,
      image: service.image,
      type: 'article'
    });
  }, [service]);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const url = pageUrl(`/services/${service.slug}`);
  const faqs = service.faqs || [];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        '@id': `${url}#service`,
        name: service.title,
        alternateName: service.headline,
        description: service.seoDescription,
        serviceType: service.title,
        url,
        image: `${SITE_URL}${service.image}`,
        areaServed: [
          { '@type': 'City', name: 'Satara' },
          { '@type': 'State', name: 'Maharashtra' },
          { '@type': 'Country', name: 'India' }
        ],
        provider: {
          '@type': ['Organization', 'LocalBusiness', 'ProfessionalService'],
          name: 'Growmore IT Services',
          url: `${SITE_URL}/`,
          telephone: '+917350695792',
          email: 'info@growmoreitservices.com',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Mangalmurti Apartment, Near IT Park, Godoli',
            addressLocality: 'Satara',
            addressRegion: 'Maharashtra',
            addressCountry: 'IN'
          }
        },
        offers: {
          '@type': 'Offer',
          url,
          availability: 'https://schema.org/InStock',
          areaServed: 'IN'
        }
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE_URL}/services` },
          { '@type': 'ListItem', position: 3, name: service.title, item: url }
        ]
      },
      faqs.length
        ? {
            '@type': 'FAQPage',
            mainEntity: faqs.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a }
            }))
          }
        : null
    ].filter(Boolean)
  };

  const related = services.filter((item) => item.slug !== service.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="pt-[72px] sm:pt-[88px]">
        <div className="relative h-44 sm:h-52 md:h-64 overflow-hidden">
          <img
            src={service.image}
            alt={`${service.headline} — Growmore IT Services Satara`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/10" />
          <div className="absolute inset-0 section-max-width px-4 md:px-6 flex flex-col justify-end pb-5 sm:pb-7 md:pb-9">
            <nav aria-label="Breadcrumb" className="mb-3 sm:mb-4">
              <ol className="flex flex-wrap items-center gap-1 text-[11px] sm:text-xs font-semibold text-white/80">
                <li>
                  <Link to="/" className="hover:text-white">Home</Link>
                </li>
                <li aria-hidden="true"><CaretRight className="w-3 h-3" weight="bold" /></li>
                <li>
                  <Link to="/services" className="hover:text-white">Services</Link>
                </li>
                <li aria-hidden="true"><CaretRight className="w-3 h-3" weight="bold" /></li>
                <li className="text-white">{service.title}</li>
              </ol>
            </nav>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 mb-3 sm:mb-4 w-fit px-3.5 py-2 rounded-full bg-white/15 hover:bg-white/25 text-white text-sm font-bold backdrop-blur-md border border-white/20 smooth-transition"
            >
              <CaretLeft className="w-4 h-4" weight="bold" />
              All services
            </Link>
            <h1 className="text-display text-[1.45rem] sm:text-4xl md:text-5xl font-extrabold text-white leading-snug max-w-4xl break-words pr-1">
              {service.title}
            </h1>
          </div>
        </div>
      </div>

      <div className="section-max-width px-4 md:px-6 py-6 md:py-12">
        <div className="grid lg:grid-cols-[minmax(0,1.45fr)_minmax(280px,0.75fr)] gap-5 lg:gap-8 items-start">
          <div className="space-y-5 sm:space-y-6 min-w-0">
            <div className="rounded-2xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-700 p-4 sm:p-8 shadow-soft animate-fade-in overflow-hidden min-w-0">
              <ServiceContent service={service} headingLevel="h2" hideHero />
            </div>

            {service.productUrl && <ChatNexShowcase />}

            {faqs.length > 0 && (
              <section className="rounded-2xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-700 p-4 sm:p-8 shadow-soft">
                <h2 className="text-display text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white mb-4 sm:mb-5">
                  {service.title} FAQs
                </h2>
                <dl className="space-y-3">
                  {faqs.map((item) => (
                    <div
                      key={item.q}
                      className="rounded-xl border border-gray-100 dark:border-gray-700 px-3.5 py-3 sm:px-4 sm:py-3.5"
                    >
                      <dt className="flex items-start gap-2 font-display font-extrabold text-sm sm:text-base text-gray-900 dark:text-white">
                        <Question className={`w-4 h-4 mt-0.5 shrink-0 ${colors.text}`} weight="fill" />
                        <span>{item.q}</span>
                      </dt>
                      <dd className="mt-1.5 pl-6 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        {item.a}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {STEPS.map((step) => {
                const Icon = step.icon;
                return (
                  <div
                    key={step.title}
                    className="rounded-2xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-700 p-4 hover:-translate-y-1 hover:shadow-medium smooth-transition"
                  >
                    <Icon className={`w-6 h-6 mb-2 ${colors.text}`} weight="fill" />
                    <p className="font-display font-extrabold text-gray-900 dark:text-white text-sm mb-1">{step.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{step.text}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:sticky lg:top-28">
            <ServiceEnquiryForm service={service} />
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-10 sm:mt-12">
            <h2 className="text-display text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white mb-4 sm:mb-5">
              More IT services in Satara
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
              {related.map((item) => (
                <Link
                  key={item.slug}
                  to={`/services/${item.slug}`}
                  className="group rounded-2xl overflow-hidden bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-700 hover:shadow-large hover:-translate-y-1 smooth-transition"
                >
                  <img
                    src={item.image}
                    alt={`${item.title} in Satara — Growmore IT Services`}
                    className="h-28 sm:h-32 w-full object-cover group-hover:scale-105 smooth-transition duration-500"
                  />
                  <div className="p-4">
                    <h3 className={`font-display font-extrabold mb-1 ${getServiceColors(item.color).text}`}>{item.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{item.short}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceDetail;
